import { handleActions, combineActions } from 'redux-actions'
import * as orderActions from '../actions/order'
import * as publicActions from '../actions/public'
import * as userActions from '../actions/user'

const initialState = {
  items: [],
  orderSummary: {
    data: null,
    loading: false,
    failed: false,
    succeed: false
  },
  orderConfirmation: {
    data: null,
    loading: false,
    failed: false,
    succeed: false
  },
  tip: 0,
  selectedDay: null,
  selectedTimeslot: null,
  zipcode: null,
  dropoff: 'Leave with doorman',
  selectedAddress: null,
  selectedCard: null,
  coupon: {
    code: '',
    loading: false,
    failed: false,
    succeed: false
  }
}

export default handleActions(
  {
    [orderActions.addProduct]: (state, action) => {
      const { items } = state
      const product = action.payload

      const found = items.findIndex(p => p.id === product.id)

      if (found !== -1) {
        return {
          ...state,
          items: [
            ...items.slice(0, found),
            { ...items[found], qty: items[found].qty + 1 },
            ...items.slice(found + 1)
          ]
        }
      }
      return {
        ...state,
        items: [...items, { ...product, qty: 1 }]
      }
    },
    [orderActions.removeProduct]: (state, action) => {
      const { items } = state
      const product = action.payload
      const found = items.find(p => product.id === p.id)

      if (found) {
        if (found.qty === 1) {
          return {
            ...state,
            items: items.filter(p => product.id !== p.id)
          }
        } else {
          return {
            ...state,
            items: items.map(p => {
              if (product.id === p.id) {
                return {
                  ...p,
                  qty: p.qty - 1
                }
              }
              return p
            })
          }
        }
      }

      return state
    },
    [orderActions.clearOrder]: state => {
      if (state.items.length) {
        return {
          ...state,
          items: []
        }
      }
      return state
    },
    [combineActions(
      orderActions.getOrderSummary,
      orderActions.applyCoupon
    )]: state => ({
      ...state,
      orderSummary: {
        ...state.orderSummary,
        loading: true,
        failed: false,
        succeed: false
      }
    }),
    [combineActions(
      orderActions.getOrderSummarySuccess,
      orderActions.applyCouponSuccess
    )]: (state, action) => ({
      ...state,
      orderSummary: {
        ...state.orderSummary,
        data: action.payload,
        loading: false,
        failed: false,
        succeed: true
      }
    }),
    [combineActions(
      orderActions.getOrderSummaryFail,
      orderActions.applyCouponFail
    )]: state => ({
      ...state,
      orderSummary: {
        ...state.orderSummary,
        loading: false,
        failed: true,
        succeed: false
      }
    }),
    [publicActions.getUpcomingDaysSuccess]: (state, action) => {
      const data = action.payload

      const currentDate = state.selectedDay ? state.selectedDay.date : null
      const currentDisplayDate = state.selectedDay
        ? state.selectedDay.displayDate
        : null

      const currentUpcomingDay = data.find(
        d => d.date === currentDate && d.displayDate === currentDisplayDate
      )
      const firstAvailableUpcomingDay = data.find(d => d.available)
      const newSelectedDay =
        currentUpcomingDay || firstAvailableUpcomingDay || null

      const newDate = newSelectedDay ? newSelectedDay.date : null
      const newDisplayDate = newSelectedDay ? newSelectedDay.displayDate : null

      if (currentDate !== newDate || currentDisplayDate !== newDisplayDate) {
        return {
          ...state,
          selectedDay: newSelectedDay
        }
      }
      return state
    },
    [orderActions.changeSelectedDeliveryDay]: (state, action) => {
      const { payload } = action
      const currentDate = state.selectedDay ? state.selectedDay.date : null
      const currentDisplayDate = state.selectedDay
        ? state.selectedDay.displayDate
        : null
      const newDate = payload ? payload.date : null
      const newDisplayDate = payload ? payload.displayDate : null

      if (currentDate !== newDate || currentDisplayDate !== newDisplayDate) {
        return {
          ...state,
          selectedDay: action.payload
        }
      }
      return state
    },
    [publicActions.getTimeslotsSuccess]: (state, action) => {
      const data = action.payload

      const currentStart = state.selectedTimeslot
        ? state.selectedTimeslot.start
        : null
      const currentEnd = state.selectedTimeslot
        ? state.selectedTimeslot.end
        : null

      const currentTimeslot = data.find(
        t => t.start === currentStart && t.end === currentEnd
      )
      const firstTimeslot = data.length ? data[0] : null

      const newSelectedTimeslot = currentTimeslot || firstTimeslot || null

      const newStart = newSelectedTimeslot ? newSelectedTimeslot.start : null
      const newEnd = newSelectedTimeslot ? newSelectedTimeslot.end : null

      if (currentStart !== newStart || currentEnd !== newEnd) {
        return {
          ...state,
          selectedTimeslot: newSelectedTimeslot
        }
      }
      return state
    },
    [orderActions.changeSelectedDeliveryTime]: (state, action) => {
      const { payload } = action
      const currentStart = state.selectedTimeslot
        ? state.selectedTimeslot.start
        : null
      const currentEnd = state.selectedTimeslot
        ? state.selectedTimeslot.end
        : null
      const newStart = payload ? payload.start : null
      const newEnd = payload ? payload.end : null

      if (currentStart !== newStart || currentEnd !== newEnd) {
        return {
          ...state,
          selectedTimeslot: payload
        }
      }
      return state
    },
    [orderActions.setZipcode]: (state, action) => {
      const currentZipcode = state.zipcode
      const newZipcode = action.payload

      if (currentZipcode !== newZipcode) {
        return {
          ...state,
          zipcode: action.payload
        }
      }

      return state
    },
    [orderActions.changeTip]: (state, action) => ({
      ...state,
      tip: action.payload
    }),
    [orderActions.changeDropoff]: (state, action) => ({
      ...state,
      dropoff: action.payload
    }),
    [combineActions(
      userActions.getAddressesSuccess,
      userActions.removeAddressSuccess
    )]: (state, action) => {
      const addresses = action.payload
      const selectedAddress = addresses.length ? addresses[0] : null
      const zipcode = addresses.length ? addresses[0].postcode : state.zipcode

      return {
        ...state,
        selectedAddress,
        zipcode
      }
    },
    [combineActions(
      userActions.createAddressSuccess,
      orderActions.changeSelectedAddress
    )]: (state, action) => {
      const zipcode = action.payload.postcode

      return {
        ...state,
        selectedAddress: action.payload,
        zipcode
      }
    },
    [combineActions(
      userActions.getCardsSuccess,
      userActions.removeCardSuccess
    )]: (state, action) => {
      const cards = action.payload
      const selectedCard = cards.length ? cards[0] : null

      return {
        ...state,
        selectedCard
      }
    },
    [combineActions(
      userActions.createCardSuccess,
      orderActions.changeSelectedCard
    )]: (state, action) => ({
      ...state,
      selectedCard: action.payload
    }),
    [orderActions.createOrder]: state => ({
      ...state,
      orderConfirmation: {
        ...state.orderConfirmation,
        loading: true,
        succeed: false,
        failed: false
      }
    }),
    [orderActions.createOrderSuccess]: (state, action) => ({
      ...state,
      items: [],
      orderSummary: {
        data: null,
        loading: false,
        failed: false,
        succeed: false
      },
      coupon: {
        code: '',
        loading: false,
        failed: false,
        succeed: false
      },
      orderConfirmation: {
        ...state.orderConfirmation,
        data: {
          ...action.payload,
          items: state.items,
          coupon: state.orderSummary.data.coupon
        },
        loading: false,
        succeed: true,
        failed: false
      }
    }),
    [orderActions.createOrderFail]: state => ({
      ...state,
      orderConfirmation: {
        ...state.orderConfirmation,
        loading: false,
        succeed: false,
        failed: true
      }
    }),
    [orderActions.changeCoupon]: (state, action) => ({
      ...state,
      coupon: {
        ...state.coupon,
        code: action.payload
      }
    }),
    [orderActions.applyCoupon]: (state, action) => ({
      ...state,
      coupon: {
        ...state.coupon,
        loading: true,
        succeed: false,
        failed: false
      }
    }),
    [orderActions.applyCouponSuccess]: (state, action) => ({
      ...state,
      coupon: {
        ...state.coupon,
        loading: false,
        succeed: true,
        failed: false
      }
    }),
    [orderActions.applyCouponFail]: (state, action) => ({
      ...state,
      coupon: {
        ...state.coupon,
        loading: false,
        succeed: false,
        failed: true
      }
    })
  },
  initialState
)



// WEBPACK FOOTER //
// ./src/store/reducers/order.js